import { IFeature } from "./../models/admin/features/features";
import { Buffer } from "buffer";
import { countryCurrency } from "../constants/Constants";
import { ICookie } from "../models/user/common/Common";
import { IPasswordConfigData } from "../admin/PasswordConfiguration/passwordConfigurationType";

export const setCookie = (data: ICookie[]) => {
  if (data.length <= 0) {
    return;
  }
  data.forEach((item: ICookie) => {
    document.cookie = `${item.cookieName}=${
      item.cookieValue
    }; ${getCookieExpiry()}`;
  });
};

export const getCookie = (name: string) => {
  const cookies = document.cookie.split(";");
  let cookieValue = "";
  cookies.forEach((item) => {
    if (item.search(name) !== -1) {
      cookieValue = item.split("=")[1];
    }
  });
  return cookieValue;
};

export const deleteCookie = (...names: any) => {
  names.forEach((item: string) => {
    document.cookie = `${item}= ; expires = Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  });
};

export const getCookieExpiry = (days: number = 1) => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = "expires=" + date.toUTCString();

  return expires;
};

export const formatNumber: (number: number | undefined) => string = (
  number
) => {
  return (
    number?.toLocaleString("en-US", {
      minimumIntegerDigits: 2,
      useGrouping: false,
    }) || ""
  );
};

export const currencyFormat = (countryCode: string, formatNumber: number) => {
  if (isNaN(formatNumber)) return "-";
  const selectedCountry: any =
    countryCurrency[countryCode as keyof typeof countryCurrency];
  return new Intl.NumberFormat(selectedCountry.name, {
    style: "currency",
    currency: selectedCountry.currency,
  }).format(formatNumber);
};

export const getBufferData = (fileData: any) => {
  const base64Str = Buffer.from(fileData).toString("base64");
  const binaryString = window.atob(base64Str);
  let bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    const ascii = binaryString.charCodeAt(i);
    bytes[i] = ascii;
  }
  return bytes;
};

export function formatDateToYYYYMMDD(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export const formatBase64String = (
  base64: string,
  mimeType: string
): string => {
  return `data:${mimeType};base64,${base64}`;
};

export const base64ToFile = (
  base64: string,
  filename: string
): File | undefined => {
  const arr = base64.split(",");

  if (arr.length !== 2 || !arr[0].startsWith("data:")) {
    console.error("Invalid base64 string format.");
    return undefined;
  }

  const mimeMatch = arr[0].match(/:(.*?);/);

  if (!mimeMatch) {
    console.error("Invalid base64 string, no mime type found.");
    return undefined;
  }
  const mime = mimeMatch[1];
  const bstr = atob(arr[1]);
  const n = bstr.length;
  const u8arr = new Uint8Array(n);

  for (let i = 0; i < n; i++) {
    u8arr[i] = bstr.charCodeAt(i);
  }

  return new File([u8arr], filename, { type: mime });
};

export const isFeatureAvailable = ({
  features,
  featureCodes,
}: {
  features: IFeature[];
  featureCodes: string[];
}): boolean => {
  return features.some(
    (feature) =>
      featureCodes?.some(
        (featureCode) => featureCode === feature.featureCode
      ) && feature.selected
  );
};

export const clearAllCookies = () => {
  const cookies = document.cookie.split(";");
  cookies.forEach((cookie) => {
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  });
};

export const capitalizeFirstLetter = (str: string) => {
  if (str.length === 0) {
    return str;
  }
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const getFileSizeWithUnits = (size: number) => {
  const fileSizeInKB = size / 1024;
  if (fileSizeInKB > 1024) {
    return (fileSizeInKB / 1024).toFixed(2) + "Mb";
  } else {
    return fileSizeInKB.toFixed(2) + "Kb";
  }
};

export const getDocTypeByTemplateIdOrName = (
  templateId: string,
  name: string
) => {
  if (!templateId || !name) return;
  if (
    templateId === "New Business Declaration" ||
    name?.toLowerCase()?.includes("renewal") ||
    templateId?.toLowerCase()?.includes("renewal")
  )
    return "NB_Package";
  if (
    templateId === "Cash With Application Receipt" ||
    templateId === "cashreceipt" ||
    templateId === "Installment Invoice" ||
    name?.toLowerCase()?.includes("receipt") ||
    name?.toLowerCase()?.includes("invoice")
  )
    return "Invoice";
  if (templateId === "Proof of Insurance Cards") return "Insurance_card";
  if (templateId === "Endorsement Declaration") return "Endorsement_Package";
};

export const createPasswordValidationRegex = (policy: IPasswordConfigData) => {
  const patterns = [];

  // Exact number of uppercase letters
  if (policy.upperCase && policy.upperCaseValue) {
    patterns.push(`(?=(?:.*[A-Z]){${policy.upperCaseValue}})`);
    patterns.push(`(?!.*[A-Z]{${policy.upperCaseValue + 1},})`);
  }

  // Exact number of lowercase letters
  if (policy.lowerCase && policy.lowerCaseValue) {
    patterns.push(`(?=(?:.*[a-z]){${policy.lowerCaseValue}})`);
    patterns.push(`(?!.*[a-z]{${policy.lowerCaseValue + 1},})`);
  }

  // Exact number of numbers
  if (policy.numericCharacters && policy.numericCharactersValue) {
    patterns.push(`(?=(?:.*[0-9]){${policy.numericCharactersValue}})`);
    patterns.push(`(?!.*[0-9]{${policy.numericCharactersValue + 1},})`);
  }

  // Exact number of special characters
  if (
    policy.specialCharacters &&
    policy.specialCharactersValue &&
    policy.specialCharactersLength
  ) {
    const escapedSpecialChars = policy.specialCharactersValue
      .split("")
      .map((char) => `\\${char}`)
      .join("");
    patterns.push(
      `(?=(?:.*[${escapedSpecialChars}]){${policy.specialCharactersLength}})`
    );
    patterns.push(
      `(?!.*[${escapedSpecialChars}]{${policy.specialCharactersLength + 1},})`
    );
    // Ensure no other special characters are used
    patterns.push(`(?!.*[^A-Za-z0-9${escapedSpecialChars}])`);
  }

  // Length requirements
  patterns.push(`^.{${policy.minimumLength},${policy.maximumLength}}$`);

  return new RegExp(patterns.join(""));
};

export const getPasswordValidationMessage = (
  password: string,
  policy: IPasswordConfigData
): string => {
  if (!password) return "";

  const errors: string[] = [];

  const pluralize = (word: string, count: number) =>
    count === 1 ? word : `${word}s`;

  // Check length
  if (
    password.length < policy.minimumLength ||
    password.length > policy.maximumLength
  ) {
    errors.push(
      `${policy.minimumLength}-${policy.maximumLength} characters long`
    );
  }

  // Check uppercase
  if (policy.upperCase && policy.upperCaseValue) {
    const upperCount = (password.match(/[A-Z]/g) || []).length;
    if (upperCount < policy.upperCaseValue) {
      errors.push(
        `at least ${policy.upperCaseValue} uppercase ${pluralize(
          "letter",
          policy.upperCaseValue
        )}`
      );
    }
  }

  // Check lowercase
  if (policy.lowerCase && policy.lowerCaseValue) {
    const lowerCount = (password.match(/[a-z]/g) || []).length;
    if (lowerCount < policy.lowerCaseValue) {
      errors.push(
        `at least ${policy.lowerCaseValue} lowercase ${pluralize(
          "letter",
          policy.lowerCaseValue
        )}`
      );
    }
  }

  // Check numbers
  if (policy.numericCharacters && policy.numericCharactersValue) {
    const numCount = (password.match(/[0-9]/g) || []).length;
    if (numCount < policy.numericCharactersValue) {
      errors.push(
        `at least ${policy.numericCharactersValue} ${pluralize(
          "number",
          policy.numericCharactersValue
        )}`
      );
    }
  }

  // Check special characters
  if (
    policy.specialCharacters &&
    policy.specialCharactersValue &&
    policy.specialCharactersLength
  ) {
    const specialChars = policy.specialCharactersValue.split("");
    const specialCharCount = password
      .split("")
      .filter((char) => specialChars.includes(char)).length;

    if (specialCharCount < policy.specialCharactersLength) {
      errors.push(
        `at least ${policy.specialCharactersLength} special ${pluralize(
          "character",
          policy.specialCharactersLength
        )} (${policy.specialCharactersValue})`
      );
    }

    // Check if any other special characters are used
    const invalidSpecialChars = password
      .split("")
      .filter(
        (char) => !char.match(/[A-Za-z0-9]/) && !specialChars.includes(char)
      );

    if (invalidSpecialChars.length > 0) {
      errors.push(
        `only allowed special characters are ${policy.specialCharactersValue}`
      );
    }
  }

  return errors.length > 0 ? `Password must be ${errors.join(", ")}` : "";
};
